/**
 * Ready-to-Ship MAC-validation window (C2b)
 *
 * Shown when a quote card is dragged to the "Ready to Ship" stage on the operations kanban
 * (a plain move to "Shipping" advances directly — no gate). Pre-fills from the flashed
 * transmitters for the quote (C1 — keyed on `transmitter.quote_number`, stamped at flash time
 * and never cleared when the device comes online, so a device missing here was never stamped)
 * merged with the already-confirmed device set (C2a, durable). Lets the user confirm the exact
 * device set before the card is allowed to advance.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useSession } from '@/hooks/useSession';
import {
  useTransmittersByQuote,
  useQuoteDevices,
  useConfirmQuoteDevices,
} from '../hooks/useOperationsMetrics';
import type { DeviceConfirmInput } from '../api';

export interface ShippingMacValidationDialogProps {
  open: boolean;
  quoteId: string | undefined;
  quoteNumber: string | null | undefined;
  onConfirm: () => void;
  onCancel: () => void;
}

interface DeviceCandidate {
  devId: string;
  name?: string;
  product?: string;
  deviceType?: string;
  sensorTech?: string;
  alreadyConfirmed: boolean;
  confirmedByName?: string | null;
}

type GroupKey = 'oneWire' | 'rtd' | 'other';

const GROUP_ORDER: GroupKey[] = ['oneWire', 'rtd', 'other'];

function classifyDevice(candidate: DeviceCandidate): { group: GroupKey; sensorTech?: string } {
  const text = `${candidate.product ?? ''} ${candidate.name ?? ''} ${candidate.deviceType ?? ''} ${candidate.sensorTech ?? ''}`.toUpperCase();
  if (/\b1W\b|ONE[- ]?WIRE/.test(text)) return { group: 'oneWire', sensorTech: '1w' };
  if (/\bRTD\b/.test(text)) return { group: 'rtd', sensorTech: 'rtd' };
  return { group: 'other' };
}

export function ShippingMacValidationDialog({
  open,
  quoteId,
  quoteNumber,
  onConfirm,
  onCancel,
}: ShippingMacValidationDialogProps) {
  const { t } = useTranslation('operations');
  const { user } = useSession();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const initializedForRef = useRef<string | null>(null);

  const {
    data: transmittersData,
    isLoading: transmittersLoading,
    isError: transmittersFailed,
  } = useTransmittersByQuote(quoteNumber ?? undefined, open);
  const {
    data: devicesData,
    isLoading: devicesLoading,
    isError: devicesFailed,
  } = useQuoteDevices(quoteId, open);
  const confirmDevices = useConfirmQuoteDevices();

  const transmitters = useMemo(() => transmittersData?.transmitters ?? [], [transmittersData]);
  const confirmedDevices = useMemo(() => devicesData?.devices ?? [], [devicesData]);

  // Merge C1 (flashed, transient) with C2a (confirmed, durable) so an already-confirmed
  // device never disappears from the checklist just because it came online in the meantime.
  const candidates = useMemo<DeviceCandidate[]>(() => {
    const confirmedByDevId = new Map(confirmedDevices.map((d) => [d.dev_id, d]));
    const map = new Map<string, DeviceCandidate>();

    for (const t of transmitters) {
      const confirmed = confirmedByDevId.get(t.dev_id);
      map.set(t.dev_id, {
        devId: t.dev_id,
        name: t.name,
        product: t.product,
        deviceType: confirmed?.device_type ?? undefined,
        sensorTech: confirmed?.sensor_tech ?? undefined,
        alreadyConfirmed: !!confirmed,
        confirmedByName: confirmed?.confirmed_by_name,
      });
    }
    for (const d of confirmedDevices) {
      if (map.has(d.dev_id)) continue;
      map.set(d.dev_id, {
        devId: d.dev_id,
        deviceType: d.device_type ?? undefined,
        sensorTech: d.sensor_tech ?? undefined,
        alreadyConfirmed: true,
        confirmedByName: d.confirmed_by_name,
      });
    }
    return Array.from(map.values());
  }, [transmitters, confirmedDevices]);

  const isLoading = transmittersLoading || devicesLoading;

  // A failed lookup must NEVER masquerade as "no devices configured". That ambiguity is
  // exactly what hid a silent, empty-bodied 403 on the transmitters route: operations users
  // were told their configured quote had no devices. Surface the failure and fail closed —
  // an unreadable device list is not a validated one, so confirming stays blocked.
  const loadFailed = transmittersFailed || devicesFailed;

  // Pre-check: the confirmed set if any, else all flashed candidates. Re-runs once per
  // (open, quoteId) pair — once the user starts unchecking boxes, a background refetch
  // must not stomp on their in-progress selection.
  useEffect(() => {
    if (!open) {
      initializedForRef.current = null;
      return;
    }
    if (isLoading) return;
    if (initializedForRef.current === quoteId) return;
    initializedForRef.current = quoteId ?? null;

    const confirmedIds = new Set(confirmedDevices.map((d) => d.dev_id));
    const candidateIds = candidates.map((c) => c.devId);
    setChecked(
      new Set(confirmedIds.size > 0 ? candidateIds.filter((id) => confirmedIds.has(id)) : candidateIds),
    );
  }, [open, quoteId, isLoading, candidates, confirmedDevices]);

  const grouped = useMemo(() => {
    const groups: Record<GroupKey, DeviceCandidate[]> = { oneWire: [], rtd: [], other: [] };
    for (const candidate of candidates) {
      const { group } = classifyDevice(candidate);
      groups[group].push(candidate);
    }
    return groups;
  }, [candidates]);

  const groupLabels: Record<GroupKey, string> = {
    oneWire: t('shippingValidation.groupOneWire'),
    rtd: t('shippingValidation.groupRtd'),
    other: t('shippingValidation.groupOther'),
  };

  const toggle = (devId: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(devId)) next.delete(devId);
      else next.add(devId);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!quoteId || loadFailed) return;
    const selected = candidates.filter((c) => checked.has(c.devId));
    const devices: DeviceConfirmInput[] = selected.map((c) => {
      const { sensorTech } = classifyDevice(c);
      return {
        devId: c.devId,
        deviceType: c.product ?? c.deviceType,
        sensorTech: c.sensorTech ?? sensorTech,
      };
    });

    const confirmedByName = user ? `${user.firstname} ${user.lastname}`.trim() || undefined : undefined;

    try {
      await confirmDevices.mutateAsync({ quoteId, devices, confirmedByName });
      onConfirm();
    } catch {
      toast.error(t('shippingValidation.error'));
    }
  };

  const isEmpty = !isLoading && !loadFailed && candidates.length === 0;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('shippingValidation.title')}</DialogTitle>
          <DialogDescription>
            {t('shippingValidation.description', { quoteNumber: quoteNumber || '—' })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('shippingValidation.loading')}
            </div>
          ) : loadFailed ? (
            <p className="text-sm text-destructive py-4">{t('shippingValidation.loadError')}</p>
          ) : isEmpty ? (
            <p className="text-sm text-muted-foreground py-4">{t('shippingValidation.empty')}</p>
          ) : (
            GROUP_ORDER.filter((key) => grouped[key].length > 0).map((key) => (
              <div key={key} className="space-y-2">
                <h4 className="text-sm font-medium">{groupLabels[key]}</h4>
                <div className="space-y-1.5">
                  {grouped[key].map((candidate) => (
                    <label
                      key={candidate.devId}
                      className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm cursor-pointer hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={checked.has(candidate.devId)}
                        onCheckedChange={() => toggle(candidate.devId)}
                      />
                      <span className="font-mono">{candidate.devId}</span>
                      {candidate.name && (
                        <span className="text-muted-foreground truncate">{candidate.name}</span>
                      )}
                      {candidate.alreadyConfirmed && (
                        <Badge variant="secondary" className="ml-auto text-[10px]">
                          {t('shippingValidation.alreadyConfirmed')}
                        </Badge>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {!isLoading && candidates.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('shippingValidation.selectedCount', { count: checked.size })}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={confirmDevices.isPending}>
            {t('shippingValidation.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={confirmDevices.isPending || isLoading || loadFailed}>
            {confirmDevices.isPending ? t('shippingValidation.confirming') : t('shippingValidation.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
